import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * Automatically adds/removes the free "Milk 2 Glow Collagène" gift item when
 * the customer has explicitly picked the "2 achetés = 1 offert" tier (tier 3
 * of the .tier-offer-selector block, see assets/tier-offer-selector.js).
 * Mirrors the real "Bundle : 2 achetés = Milk 2 Glow Collagène offert"
 * automatic discount configured in Shopify (Settings > Discounts), which
 * prices this line at 0€ whenever the condition is met - this script's only
 * job is to make sure the item is actually present in the cart, and that
 * there's exactly one of it, without the customer having to add it manually.
 *
 * Why this reads a cart attribute instead of a raw quantity threshold: tiers
 * 2 ("2 Gommages", no gift) and 3 ("2 Gommages + 1 offert", gift) both add 2
 * units of the SAME product, so the cart state for the eligible product line
 * is identical in both cases. tier-offer-selector.js records the customer's
 * real intent as the cart-level attribute "_tier_gift_intent" on the
 * add-to-cart submission; the gift is only added when that attribute is
 * "true" AND at least 2 eligible units are actually in the cart (so a stale
 * attribute from an earlier session never grants a gift on its own).
 */

const GIFT_VARIANT_ID = 54494687428941;
const GIFT_INTENT_ATTRIBUTE = '_tier_gift_intent';
const MIN_ELIGIBLE_QTY = 2;
const GIFT_PROPERTY_KEY = '_free_gift_bundle';
const SOURCE_ID = 'free-gift-bundle';

// The 10 products whose combined quantity counts toward the eligible total.
const ELIGIBLE_PRODUCT_IDS = new Set([
  10800563945805, 10800584261965, 10800584687949, 10800584917325, 10801373774157, 10801373806925, 10801373872461,
  10801374265677, 10801377706317, 10801377771853,
]);

let processing = false;

async function fetchCart() {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

function getSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

function findGiftLines(cart) {
  return cart.items.filter((item) => item.properties && item.properties[GIFT_PROPERTY_KEY] === 'true');
}

function eligibleQuantity(cart, giftLines) {
  const giftKeys = new Set(giftLines.map((l) => l.key));
  let qty = 0;
  for (const item of cart.items) {
    if (giftKeys.has(item.key)) continue;
    if (ELIGIBLE_PRODUCT_IDS.has(item.product_id)) qty += item.quantity;
  }
  return qty;
}

function hasGiftIntent(cart) {
  return Boolean(cart.attributes && cart.attributes[GIFT_INTENT_ATTRIBUTE] === 'true');
}

async function dispatchAndResolve(action, lines, request) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));

  try {
    const response = await request();
    if (response && response.status) {
      document.dispatchEvent(
        new CartErrorEvent({ error: response.description || response.message || 'Cart error', code: 'INVALID' })
      );
      deferred.reject(new Error(response.message || 'Cart error'));
      return;
    }

    const ajaxCart = await fetchCart();
    if (!ajaxCart) {
      deferred.reject(new Error('Unable to refresh cart'));
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: SOURCE_ID,
        sourceId: SOURCE_ID,
        itemCount: ajaxCart.item_count,
        sections: response ? response.sections : undefined,
        didError: false,
      },
    });
  } catch (err) {
    deferred.reject(err);
  }
}

async function addGift() {
  await dispatchAndResolve(
    'add',
    [{ merchandiseId: `gid://shopify/ProductVariant/${GIFT_VARIANT_ID}`, quantity: 1 }],
    async () => {
      const formData = new FormData();
      formData.append('id', String(GIFT_VARIANT_ID));
      formData.append('quantity', '1');
      formData.append(`properties[${GIFT_PROPERTY_KEY}]`, 'true');
      const sectionIds = getSectionIds();
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      }).then((r) => r.json());
    }
  );
}

async function removeGiftLine(line) {
  await dispatchAndResolve('remove', [{ id: line.key, quantity: 0 }], async () => {
    const sectionIds = getSectionIds();
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: line.key,
        quantity: 0,
        ...(sectionIds.length ? { sections: sectionIds } : {}),
      }),
    }).then((r) => r.json());
  });
}

async function syncGift() {
  if (processing) return;
  processing = true;
  try {
    const cart = await fetchCart();
    if (!cart) return;

    const giftLines = findGiftLines(cart);
    const qty = eligibleQuantity(cart, giftLines);
    const totalGiftQty = giftLines.reduce((sum, l) => sum + l.quantity, 0);
    const shouldHaveGift = hasGiftIntent(cart) && qty >= MIN_ELIGIBLE_QTY;

    if (shouldHaveGift && giftLines.length === 0) {
      await addGift();
    } else if (!shouldHaveGift && giftLines.length > 0) {
      for (const line of giftLines) {
        await removeGiftLine(line);
      }
    } else if (shouldHaveGift && (giftLines.length > 1 || totalGiftQty > 1)) {
      // Guard: only ever one free unit, even if something duplicated the line
      // or a quantity stepper on the gift row was used.
      for (const line of giftLines) {
        await removeGiftLine(line);
      }
      await addGift();
    }
  } finally {
    processing = false;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncGift);
} else {
  syncGift();
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (processing) return;
  event.promise?.then(() => syncGift()).catch(() => {});
});
